import React, { useId, useMemo, useState } from 'react';

const keyOf = (item) => (typeof item === 'object' ? item.key ?? item.id ?? JSON.stringify(item) : String(item));
const defaultLabel = (item) => (typeof item === 'object' ? item.label || item.name || JSON.stringify(item) : String(item));

/**
 * Two lists with buttons to move items between them. Controlled: both lists come from the props and
 * every move goes through onSelectionChange(newSelected, newAvailable), so what is shown (and
 * clicked) is always the parent's current state.
 *
 * Optional: getLabel(item) for the text shown, getGroup(item) to show the available items under
 * group headings, `searchable` for a filter over the available items, and `reorderable` for
 * Move up/down buttons on the selected list (whose order then matters, e.g. report columns).
 * Double-clicking an item moves it to the other list.
 */
export default function TransferList({
  availableItems = [],
  selectedItems = [],
  onSelectionChange,
  availableTitle = "Available",
  selectedTitle = "Selected",
  getLabel = defaultLabel,
  getGroup,
  searchable = false,
  reorderable = false,
}) {
  const available = availableItems;
  const selected = selectedItems;
  const [selectedAvailableItem, setSelectedAvailableItem] = useState(null);
  const [selectedSelectedItem, setSelectedSelectedItem] = useState(null);
  const [query, setQuery] = useState('');
  const searchId = useId();

  const change = (newSelected, newAvailable) => onSelectionChange?.(newSelected, newAvailable);

  const q = query.trim().toLowerCase();
  const shownAvailable = useMemo(
    () => (q ? available.filter((item) => getLabel(item).toLowerCase().includes(q)) : available),
    [available, q, getLabel]
  );
  // [{ group, items }] in order of first appearance; one unnamed group without getGroup
  const availableGroups = useMemo(() => {
    const groups = new Map();
    for (const item of shownAvailable) {
      const group = getGroup ? getGroup(item) : '';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(item);
    }
    return [...groups].map(([group, items]) => ({ group, items }));
  }, [shownAvailable, getGroup]);

  // Move item from available to selected
  const moveToSelected = (item) => {
    setSelectedAvailableItem(null);
    change([...selected, item], available.filter(i => i !== item));
  };

  // Move item from selected to available
  const moveToAvailable = (item) => {
    setSelectedSelectedItem(null);
    change(selected.filter(i => i !== item), [...available, item]);
  };

  // Move all (shown) items to selected
  const moveAllToSelected = () => {
    setSelectedAvailableItem(null);
    change([...selected, ...shownAvailable], available.filter(i => !shownAvailable.includes(i)));
  };

  // Move all items to available
  const moveAllToAvailable = () => {
    setSelectedSelectedItem(null);
    change([], [...available, ...selected]);
  };

  const moveSelectedBy = (offset) => {
    const from = selected.indexOf(selectedSelectedItem);
    const to = from + offset;
    if (from < 0 || to < 0 || to >= selected.length) return;
    const reordered = [...selected];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    change(reordered, available);
  };

  // Handle clicking on available item
  const handleAvailableItemClick = (item) => {
    setSelectedAvailableItem(item);
    setSelectedSelectedItem(null); // Clear other selection
  };

  // Handle clicking on selected item
  const handleSelectedItemClick = (item) => {
    setSelectedSelectedItem(item);
    setSelectedAvailableItem(null); // Clear other selection
  };

  // Move selected available item to selected
  const moveSelectedToSelected = () => {
    if (selectedAvailableItem) {
      moveToSelected(selectedAvailableItem);
    }
  };

  // Move selected selected item to available
  const moveSelectedToAvailable = () => {
    if (selectedSelectedItem) {
      moveToAvailable(selectedSelectedItem);
    }
  };

  const itemClass = (highlighted) => `p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer select-none ${
    highlighted ? 'bg-blue-100 dark:bg-blue-900 border border-blue-300 dark:border-blue-700' : ''
  }`;
  const selectedIndex = selected.indexOf(selectedSelectedItem);

  return (
    <div className="flex gap-4 items-start">
      {/* Available Items List */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold mb-2 text-gray-700 dark:text-gray-300">
          {availableTitle} ({available.length})
        </h3>
        {searchable && (
          <>
            <label htmlFor={searchId} className="sr-only">Search {availableTitle.toLowerCase()}</label>
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="mb-2 w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </>
        )}
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 min-h-[200px] max-h-72 overflow-y-auto">
          {shownAvailable.length === 0 ? (
            <div className="p-4 text-center text-gray-400 dark:text-gray-500 text-sm">
              {q ? 'No matching items' : 'No available items'}
            </div>
          ) : (
            <div className="p-2">
              {availableGroups.map(({ group, items }) => (
                <div key={group || 'items'} role="group" aria-label={group || undefined}>
                  {group && (
                    <div className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{group}</div>
                  )}
                  {items.map((item) => (
                    <div
                      key={`available-${keyOf(item)}`}
                      className={itemClass(selectedAvailableItem === item)}
                      onClick={() => handleAvailableItemClick(item)}
                      onDoubleClick={() => moveToSelected(item)}
                    >
                      <span className="text-sm text-gray-900 dark:text-gray-100">{getLabel(item)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Arrow Buttons */}
      <div className="flex flex-col gap-2 mt-8">
        <button
          onClick={moveAllToSelected}
          disabled={shownAvailable.length === 0}
          className="px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm"
          title="Move all to selected"
        >
          »
        </button>
        <button
          onClick={moveSelectedToSelected}
          disabled={!selectedAvailableItem}
          className="px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm"
          title="Move selected to selected"
        >
          →
        </button>
        <button
          onClick={moveSelectedToAvailable}
          disabled={!selectedSelectedItem}
          className="px-3 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm"
          title="Move selected to available"
        >
          ←
        </button>
        <button
          onClick={moveAllToAvailable}
          disabled={selected.length === 0}
          className="px-3 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm"
          title="Move all to available"
        >
          «
        </button>
        {reorderable && (
          <>
            <button
              onClick={() => moveSelectedBy(-1)}
              disabled={selectedIndex <= 0}
              className="mt-4 px-3 py-2 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm"
              title="Move up"
            >
              ↑
            </button>
            <button
              onClick={() => moveSelectedBy(1)}
              disabled={selectedIndex < 0 || selectedIndex >= selected.length - 1}
              className="px-3 py-2 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm"
              title="Move down"
            >
              ↓
            </button>
          </>
        )}
      </div>

      {/* Selected Items List */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold mb-2 text-gray-700 dark:text-gray-300">
          {selectedTitle} ({selected.length})
        </h3>
        {searchable && <div className="mb-2 h-[34px]" aria-hidden="true" />}
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 min-h-[200px] max-h-72 overflow-y-auto">
          {selected.length === 0 ? (
            <div className="p-4 text-center text-gray-400 dark:text-gray-500 text-sm">
              No selected items
            </div>
          ) : (
            <ol className="p-2" aria-label={selectedTitle}>
              {selected.map((item) => (
                <li
                  key={`selected-${keyOf(item)}`}
                  className={itemClass(selectedSelectedItem === item)}
                  onClick={() => handleSelectedItemClick(item)}
                  onDoubleClick={() => moveToAvailable(item)}
                >
                  <span className="text-sm text-gray-900 dark:text-gray-100">{getLabel(item)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
